import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';
import type { TTableCellContent } from '../atoms';
import { TableCellContent } from '../atoms';
import { ChevronLeftIcon, ChevronRightIcon } from '../atoms/icons';
import {
  ApplicationModal,
  EditUserModal,
  LoanModal,
  RequestDifferentTermModal,
  RequestFundModal,
  CourseEndDateModal
} from '../molecules';
import type { ReactElement } from 'react';
import { useState } from 'react';
import type { TApplication, TPagination } from '../types';
import type { Prettify, TButtonType, TLoan } from '../../common';
import { defaultPagination } from '../../common';
import type { Merge } from 'simplytyped';
import { ActivatePlanModal } from './ActivatePlanModal';
import { FundingStatus } from '../atoms/FundingStatus';
import { CourseEndDate } from '../atoms/CourseEndDate';
import React from 'react';

const buttonWidth = 200; // px
const buttonWidthMd = 10; // rem
const buttonWidthXs = 11; // rem

type TDisplayTable = {
  colWidths: Array<string>;
  colWidthsMd: Array<string>;
  children: React.ReactNode;
  hasButton?: boolean;
};

type TTable = {
  gridConfig: string;
  gridConfigSm: string;
  hasButton?: boolean;
};

type TDisplayTableHead = {
  cols: Array<string>;
};

type TDisplayTablePaginationRow = {
  pagination: TPagination;
  setPage: (newPage: number) => void;
};

type TDisplayTableRow = {
  type: 'link' | 'modal' | 'div';
  action?: string;
  cells: Prettify<Array<TTableCellContent>>;
  button?: ReactElement;
  buttonModalType: TButtonType;
  modalType?: 'application' | 'loan' | 'tenant_user' | 'platform_user';
  modalTitle?: string;
  modalAppContent?: TApplication;
  modalLoanContent?: TLoan;
  modalUserContent?: any; //Cam TODO
  showModal?: boolean;
  refreshPage?: () => void;
};

type TRowWrapper = {
  hasButton: boolean;
};

type TRowLink = {
  to?: string;
  type: 'link' | 'modal' | 'div';
};

type TTableCell = {
  breakAll?: boolean;
};

const TableHeadRow = styled.div(
  ({ theme }) => css`
    padding: 0 20px 20px 20px;
    text-align: left;

    @media (max-width: ${theme.breakpoints.max.md}) {
      padding: 0 0.9375rem 0.9375rem 0.9375rem;
    }
  `
);

const PreviousPageLink = styled(ChevronLeftIcon)(
  ({ theme }) => css`
    cursor: pointer;
    margin-right: 0.5em;
  `
);

const NextPageLink = styled(ChevronRightIcon)(
  ({ theme }) => css`
    cursor: pointer;
    margin-left: 0.5em;
  `
);

const PageLinkPlaceholder = styled.div(
  ({ theme }) => css`
    display: inline-block;
    width: 1.5em;
    height: 1em;
  `
);

const TablePaginationRow = styled.div(
  ({ theme }) => css`
    padding: 0 20px 20px 20px;
    text-align: right;

    @media (max-width: ${theme.breakpoints.max.md}) {
      padding: 0 0.9375rem 0.9375rem 0.9375rem;
    }
  `
);

const RowLink = styled(NavLink)<TRowLink>(
  ({ theme, type }) => css`
    padding: 20px;
    font-size: ${theme.fonts.sizes.text};
    color: ${theme.colors.main.font};
    background-color: ${theme.colors.white};
    border: 0;
    align-items: center;
    min-height: 97px;
    width: 100%;

    @media (max-width: ${theme.breakpoints.max.md}) {
      padding: 15px;
      min-height: 5.4375rem;
    }

    @media (max-width: ${theme.breakpoints.max.sm}) {
      font-size: ${theme.fonts.sizes.h6};
    }

    ${type !== 'div' &&
    css`
      @media (hover: hover) {
        &:hover {
          cursor: pointer;
          box-shadow: ${theme.colors.main.boxShadow};
          background-color: ${theme.colors.main.light};
        }
      }
    `}
  `
);

const RowWrapper = styled.div<TRowWrapper>(
  ({ theme, hasButton }) => css`
    background-color: ${theme.colors.white};
    align-items: center;

    ${hasButton &&
    css`
      display: grid;
      grid-template-columns: auto ${buttonWidth + 20}px;

      @media (max-width: ${theme.breakpoints.max.md}) {
        grid-template-columns: auto ${buttonWidthMd + 0.9375}rem;
      }

      @media (max-width: ${theme.breakpoints.max.xs}) {
        grid-template-columns: auto ${buttonWidthXs + 0.9375}rem;
      }

      ${RowLink} {
        padding-right: 10px;
      }
    `}
  `
);

const Table = styled.div<TTable>(
  ({ theme, gridConfig, gridConfigSm, hasButton }) => css`
    ${TableHeadRow}, ${RowLink} {
      display: grid;
      grid-gap: 20px;

      @media (max-width: ${theme.breakpoints.max.md}) {
        grid-gap: 15px;
      }
    }

    // if the table has at least one button, empty space for button on end
    ${TableHeadRow}, ${RowLink} {
      grid-template-columns: ${gridConfig} ${hasButton
          ? `${buttonWidth}px`
          : ''};

      @media (max-width: ${theme.breakpoints.max.md}) {
        grid-template-columns: ${gridConfigSm} ${hasButton
            ? `${buttonWidthMd}rem`
            : ''};
      }

      @media (max-width: ${theme.breakpoints.max.xs}) {
        grid-template-columns: ${gridConfigSm} ${hasButton
            ? `${buttonWidthXs}rem`
            : ''};
      }
    }

    // if the row has a button, no empty space for button on end
    ${RowWrapper}.has-button ${RowLink} {
      grid-template-columns: ${gridConfig};

      @media (max-width: ${theme.breakpoints.max.md}) {
        grid-template-columns: ${gridConfigSm};
      }
    }
  `
);

const TableRow = styled.div`
  text-align: left;
  padding: 0.625rem 0;
`;

const TableHead = styled.p(
  ({ theme }) => css`
    margin: 0;

    @media (max-width: ${theme.breakpoints.max.md}) {
      font-size: ${theme.fonts.sizes.h4} !important;
      line-height: 1.375rem;
    }
  `
);

const TableCell = styled.div<TTableCell>(
  ({ theme, breakAll }) => css`
    word-break: ${breakAll ? 'break-all' : 'normal'};
    text-align: left;

    @media (max-width: ${theme.breakpoints.max.md}) {
      .status-wrapper {
        font-size: 0.75rem;
        padding: 0.125rem 0.3125rem;
        display: block;
        text-align: center;
      }
    }
  `
);

const ButtonWrapper = styled.div(
  ({ theme }) => css`
    padding-left: 10px;
    padding-right: 20px;

    & > button {
      width: 100%;
    }

    @media (max-width: ${theme.breakpoints.max.md}) {
      padding-right: 15px;

      & > button {
        font-size: ${theme.fonts.sizes.h6};
        padding: 10px 15px;
      }
    }
  `
);

const DisplayTable = ({
  colWidths,
  colWidthsMd,
  children,
  hasButton
}: TDisplayTable) => {
  const gridConfig = colWidths.reduce((acc, width) => acc + `${width} `, '');

  const gridConfigSm = colWidthsMd.reduce(
    (acc, width) => acc + `${width} `,
    ''
  );

  return (
    <Table
      gridConfig={gridConfig}
      gridConfigSm={gridConfigSm}
      hasButton={hasButton}
    >
      {children}
    </Table>
  );
};

const DisplayTableHead = ({ cols }: TDisplayTableHead) => {
  return (
    <TableHeadRow>
      {cols.map((item, index) => (
        <TableHead key={item + index} className="h3">
          {item}
        </TableHead>
      ))}
    </TableHeadRow>
  );
};

const DisplayTablePaginationRow = ({
  pagination,
  setPage = () => undefined
}: TDisplayTablePaginationRow) => {
  const { currentPage, pageSize, totalCount } = {
    ...defaultPagination,
    ...pagination
  };
  const totalPages = Math.ceil(totalCount / pageSize);
  // TODO log
  // console.log(`Page ${currentPage} of ${totalPages}`, pagination);
  return (
    <TablePaginationRow>
      {currentPage > 1 ? (
        <PreviousPageLink onClick={() => setPage(currentPage - 1)} />
      ) : (
        <PageLinkPlaceholder />
      )}
      page {currentPage}
      {currentPage < totalPages ? (
        <NextPageLink onClick={() => setPage(currentPage + 1)} />
      ) : (
        <PageLinkPlaceholder />
      )}
    </TablePaginationRow>
  );
};

type TFakeFunding = {
  fundingStatus: 'Requested';
  fundingStatusText: string;
} | null;

const DisplayTableRow = ({
  type,
  action,
  cells,
  button,
  buttonModalType,
  modalType,
  modalTitle,
  modalAppContent,
  modalLoanContent,
  modalUserContent,
  showModal,
  refreshPage
}: TDisplayTableRow) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [buttonModalOpen, setButtonModalOpen] = useState(false);
  const [fakeFunding, setFakeFunding] = useState<TFakeFunding>(null);

  const handleOpen = () => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  };

  const handleClose = () => {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  };

  const handleModalOpen = () => {
    setModalOpen(true);
    handleOpen();
  };

  const handleModalClose = () => {
    setModalOpen(false);
    handleClose();
  };

  const handleButtonModalOpen = () => {
    setButtonModalOpen(true);
    handleOpen();
  };

  const handleButtonModalClose = () => {
    setButtonModalOpen(false);
    handleClose();
  };

  const shouldShowButton = (
    button: JSX.Element | undefined,
    showModal: boolean | undefined,
    buttonModalType: TButtonType,
    courseEndDate?: string | null
  ) => {
    if (!button) {
      return false;
    }
    if (
      buttonModalType !== 'requestFund' &&
      buttonModalType !== 'activatePlan' &&
      buttonModalType !== 'courseEndDate'
    ) {
      return true;
    }
    if (
      (buttonModalType === 'requestFund' ||
        buttonModalType === 'activatePlan' ||
        buttonModalType === 'courseEndDate') &&
      fakeFunding
    ) {
      return false;
    }
    if (buttonModalType === 'courseEndDate' && !!courseEndDate) {
      return false;
    }
    return showModal;
  };

  return (
    <TableRow>
      <RowWrapper
        hasButton={button ? true : false}
        className={button ? 'has-button' : ''}
      >
        <RowLink
          className={showModal ? 'show-modal-button' : 'hide-modal-button'}
          to={type === 'link' ? action : undefined}
          as={
            type === 'modal'
              ? 'button'
              : undefined || type === 'div'
                ? 'div'
                : undefined
          }
          type={type}
          onClick={type === 'modal' ? handleModalOpen : undefined}
        >
          {cells.map(({ breakAll, ...rest }, index) => {
            const tableContent = rest as TTableCellContent;

            return (
              <TableCell key={index} breakAll={breakAll}>
                <TableCellContent {...tableContent} />
              </TableCell>
            );
          })}
        </RowLink>
        {/* {button && ( */}
        {shouldShowButton(
          button,
          showModal,
          buttonModalType,
          modalLoanContent?.courseEndDate
        ) && (
          <ButtonWrapper
            onClick={buttonModalType ? handleButtonModalOpen : undefined}
          >
            {button}
            {/* Request fund modal --------------------- */}
            {buttonModalType === 'requestFund' && (
              <RequestFundModal
                modalTitle={modalTitle}
                id={
                  modalLoanContent ? modalLoanContent.originalAccountNumber : ''
                }
                loanApplicationSalesAgentUserId={
                  modalLoanContent?.salesPersonId ?? undefined
                }
                loanApplicationSalesAgentEmail={
                  modalLoanContent?.merchantContactEmail ?? undefined
                }
                creationDate={
                  modalLoanContent ? modalLoanContent.creationDate : ''
                }
                isModalOpen={buttonModalOpen}
                handleModalClose={handleButtonModalClose}
                setFakeFunding={setFakeFunding}
              />
            )}
            {/* Activate plan modal --------------------- */}
            {buttonModalType === 'activatePlan' && (
              <ActivatePlanModal
                modalTitle={modalTitle}
                id={
                  modalLoanContent ? modalLoanContent.originalAccountNumber : ''
                }
                loanApplicationSalesAgentUserId={
                  modalLoanContent?.salesPersonId ?? undefined
                }
                loanApplicationSalesAgentEmail={
                  modalLoanContent?.merchantContactEmail ?? undefined
                }
                creationDate={
                  modalLoanContent ? modalLoanContent.creationDate : ''
                }
                isModalOpen={buttonModalOpen}
                handleModalClose={handleButtonModalClose}
                setFakeFunding={setFakeFunding}
              />
            )}
            {/* Request different term modal --------------------- */}
            {buttonModalType === 'requestDifferentTerm' &&
              modalAppContent?.mambuAccountId && (
                <RequestDifferentTermModal
                  modalTitle={modalTitle}
                  id={
                    modalAppContent.salesforceAppNumber ||
                    modalAppContent.mambuAccountId
                  }
                  mambuAccountId={modalAppContent.mambuAccountId}
                  loanApplicationId={modalAppContent.loanApplicationOriginalId}
                  creationDate={
                    modalAppContent ? modalAppContent.creationDate : ''
                  }
                  isModalOpen={buttonModalOpen}
                  handleModalClose={handleButtonModalClose}
                  refreshPage={refreshPage as () => void}
                />
              )}
            {/* Course End Date modal --------------------- */}
            {buttonModalType === 'courseEndDate' && (
              <CourseEndDateModal
                modalTitle={modalTitle}
                id={
                  modalLoanContent ? modalLoanContent.originalAccountNumber : ''
                }
                loanApplicationSalesAgentUserId={
                  modalLoanContent?.salesPersonId ?? undefined
                }
                loanApplicationSalesAgentEmail={
                  modalLoanContent?.merchantContactEmail ?? undefined
                }
                creationDate={
                  modalLoanContent ? modalLoanContent.creationDate : ''
                }
                isModalOpen={buttonModalOpen}
                handleModalClose={handleButtonModalClose}
                setFakeFunding={setFakeFunding}
              />
            )}
          </ButtonWrapper>
        )}
        {!!modalLoanContent?.fundingDate &&
          modalLoanContent?.fundingStatus &&
          modalLoanContent.fundingStatusText && (
            <FundingStatus
              fundingStatus={modalLoanContent.fundingStatus}
              fundingStatusText={modalLoanContent.fundingStatusText}
              fundingDate={modalLoanContent.fundingDate}
              disbursementDate={modalLoanContent.disbursementDate}
              buttonModalType={buttonModalType}
            />
          )}
        {!!modalLoanContent?.courseEndDate && (
          <CourseEndDate courseEndDate={modalLoanContent.courseEndDate} />
        )}
        {fakeFunding && (
          <FundingStatus
            fundingStatus={fakeFunding.fundingStatus}
            fundingStatusText={fakeFunding.fundingStatusText}
            buttonModalType={buttonModalType}
          />
        )}
        {type === 'modal' && modalType === 'application' && modalAppContent && (
          <ApplicationModal
            modalTitle={modalTitle}
            content={modalAppContent}
            isModalOpen={modalOpen}
            handleModalClose={handleModalClose}
            refreshPage={refreshPage as () => void}
          />
        )}
        {type === 'modal' && modalType === 'loan' && modalLoanContent && (
          <LoanModal
            modalTitle={modalTitle}
            content={modalLoanContent}
            isModalOpen={modalOpen}
            handleModalClose={handleModalClose}
          />
        )}
        {type === 'modal' &&
          (modalType === 'platform_user' || modalType === 'tenant_user') &&
          modalUserContent && (
            <EditUserModal
              modalTitle={modalTitle}
              userType={modalType}
              content={modalUserContent}
              isModalOpen={modalOpen}
              handleModalClose={handleModalClose}
              refreshPage={refreshPage as () => void}
            />
          )}
      </RowWrapper>
    </TableRow>
  );
};

export {
  DisplayTable,
  DisplayTableHead,
  DisplayTableRow,
  DisplayTablePaginationRow
};
